// hooks/useModals.ts
import { useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Course data for semester selection
 */
export interface SemesterSelectionCourse {
  code: string;
  title?: string;
  credits?: number;
}

/**
 * Context for move operations
 */
export interface MoveContext {
  fromSemester: string;
  index: number;
}

/**
 * Placeholder data for editing
 */
export interface PlaceholderData {
  code?: string;
  title?: string;
  credits?: number;
  placeholderType?: string;
  requirements?: Record<string, unknown>;
  notes?: string;
  color?: string;
}

/**
 * Context for placeholder operations
 */
export interface PlaceholderContext {
  semester?: string;
  index?: number;
  [key: string]: unknown;
}

/**
 * Placeholder conversion data
 */
export interface PlaceholderConversionData {
  placeholder: PlaceholderData;
  fromSemester: string;
  fromIndex: number;
  [key: string]: unknown;
}

/**
 * Requirement modal data
 */
export interface RequirementModalData {
  course: SemesterSelectionCourse;
  currentRequirements: unknown[];
}

/**
 * Program selection data
 */
export interface ProgramSelectionData {
  programType?: 'major' | 'minor' | 'citation';
  currentPrograms?: string[];
  [key: string]: unknown;
}

/**
 * Options for opening the placeholder modal
 */
export interface OpenPlaceholderModalOptions {
  editingPlaceholder?: PlaceholderData | null;
  targetSemester?: string | null;
  placeholderContext?: PlaceholderContext | null;
}

/**
 * Modal visibility states
 */
export interface ModalVisibility {
  semesterSelection: boolean;
  placeholder: boolean;
  conversion: boolean;
  requirement: boolean;
  commandPalette: boolean;
  programSelection: boolean;
}

/**
 * All modal data
 */
export interface AllModalData {
  selectedCourseForSemester: SemesterSelectionCourse | null;
  moveContext: MoveContext | null;
  editingPlaceholder: PlaceholderData | null;
  placeholderTargetSemester: string | null;
  placeholderContext: PlaceholderContext | null;
  placeholderToConvert: PlaceholderConversionData | null;
  requirementModalData: RequirementModalData | null;
  programSelectionData: ProgramSelectionData | null;
}

/**
 * Modal operations
 */
export interface ModalOps {
  openSemesterSelection: (course: SemesterSelectionCourse, context?: MoveContext | null) => void;
  closeSemesterSelection: () => void;
  openPlaceholderModal: (options?: OpenPlaceholderModalOptions) => void;
  closePlaceholderModal: () => void;
  openConversionModal: (placeholderData: PlaceholderConversionData) => void;
  closeConversionModal: () => void;
  openRequirementModal: (data: RequirementModalData) => void;
  closeRequirementModal: () => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;
  openProgramSelection: (data?: ProgramSelectionData | null) => void;
  closeProgramSelection: () => void;
  closeAllModals: () => void;
}

/**
 * Return type for the useModals hook
 */
export interface UseModalsReturn {
  modals: ModalVisibility;
  modalData: AllModalData;
  modalOps: ModalOps;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to manage all modal states in the application
 */
export const useModals = (): UseModalsReturn => {
  // Semester Selection Modal
  const [selectedCourseForSemester, setSelectedCourseForSemester] = useState<SemesterSelectionCourse | null>(null);
  const [moveContext, setMoveContext] = useState<MoveContext | null>(null);

  // Placeholder Modal
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [editingPlaceholder, setEditingPlaceholder] = useState<PlaceholderData | null>(null);
  const [placeholderTargetSemester, setPlaceholderTargetSemester] = useState<string | null>(null);
  const [placeholderContext, setPlaceholderContext] = useState<PlaceholderContext | null>(null);

  // Conversion Modal
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [placeholderToConvert, setPlaceholderToConvert] = useState<PlaceholderConversionData | null>(null);

  // Requirement Modal
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [requirementModalData, setRequirementModalData] = useState<RequirementModalData | null>(null);

  // Command Palette
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Program Selection Modal
  const [showProgramSelectionModal, setShowProgramSelectionModal] = useState(false);
  const [programSelectionData, setProgramSelectionData] = useState<ProgramSelectionData | null>(null);

  // Modal Operations
  const modalOps: ModalOps = {
    // Semester Selection Modal
    openSemesterSelection: (course: SemesterSelectionCourse, context: MoveContext | null = null) => {
      setSelectedCourseForSemester(course);
      setMoveContext(context); // context = {fromSemester, index} for moves
    },

    closeSemesterSelection: () => {
      setSelectedCourseForSemester(null);
      setMoveContext(null);
    },

    // Placeholder Modal
    openPlaceholderModal: ({
      editingPlaceholder: editing = null,
      targetSemester = null,
      placeholderContext: context = null
    }: OpenPlaceholderModalOptions = {}) => {
      setEditingPlaceholder(editing);
      setPlaceholderTargetSemester(targetSemester);
      setPlaceholderContext(context);
      setShowPlaceholderModal(true);
    },

    closePlaceholderModal: () => {
      setShowPlaceholderModal(false);
      setEditingPlaceholder(null);
      setPlaceholderTargetSemester(null);
      setPlaceholderContext(null);
    },

    // Conversion Modal
    openConversionModal: (placeholderData: PlaceholderConversionData) => {
      setPlaceholderToConvert(placeholderData);
      setShowConversionModal(true);
    },

    closeConversionModal: () => {
      setShowConversionModal(false);
      setPlaceholderToConvert(null);
    },

    // Requirement Modal
    openRequirementModal: ({ course, currentRequirements }: RequirementModalData) => {
      setRequirementModalData({ course, currentRequirements });
      setShowRequirementModal(true);
    },

    closeRequirementModal: () => {
      setShowRequirementModal(false);
      setRequirementModalData(null);
    },

    // Command Palette
    openCommandPalette: () => setShowCommandPalette(true),
    closeCommandPalette: () => setShowCommandPalette(false),
    toggleCommandPalette: () => setShowCommandPalette(prev => !prev),

    // Program Selection Modal
    openProgramSelection: (data: ProgramSelectionData | null = null) => {
      setProgramSelectionData(data);
      setShowProgramSelectionModal(true);
    },

    closeProgramSelection: () => {
      setShowProgramSelectionModal(false);
      setProgramSelectionData(null);
    },

    // Utility function to close all modals
    closeAllModals: () => {
      setSelectedCourseForSemester(null);
      setMoveContext(null);
      setShowPlaceholderModal(false);
      setEditingPlaceholder(null);
      setPlaceholderTargetSemester(null);
      setPlaceholderContext(null);
      setShowConversionModal(false);
      setPlaceholderToConvert(null);
      setShowRequirementModal(false);
      setRequirementModalData(null);
      setShowCommandPalette(false);
      setShowProgramSelectionModal(false);
      setProgramSelectionData(null);
    }
  };

  // Return state and operations
  return {
    // Modal visibility states
    modals: {
      semesterSelection: !!selectedCourseForSemester,
      placeholder: showPlaceholderModal,
      conversion: showConversionModal,
      requirement: showRequirementModal,
      commandPalette: showCommandPalette,
      programSelection: showProgramSelectionModal
    },

    // Modal data
    modalData: {
      selectedCourseForSemester,
      moveContext,
      editingPlaceholder,
      placeholderTargetSemester,
      placeholderContext,
      placeholderToConvert,
      requirementModalData,
      programSelectionData
    },

    // All operations
    modalOps
  };
};
